import axios from "axios";
import AuthService from "src/services/authService";
import appConsoleService from "src/services/utils/appConsoleService";
import NavigationService from "./navigationService";
import networkService from "./networkService";
import {useCacheStore, isCacheableRequest} from "src/stores/cacheStore";
import AppEnvService from "./utils/appEnvService";
import {SESSION_EXPIRED_MESSAGE} from "./utils/appNotificationService";

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

// Handle expiration token
axios.interceptors.response.use((response) => {
  appConsoleService.info('AxiosService', `${response.config.url}: Status = ${response.status}`);
  return response
}, (error) => {
  appConsoleService.error('AxiosService', error.message)
  // Un 401 sur la connexion elle-même (mauvais mot de passe) n'est pas une session expirée : le formulaire l'affiche
  const isSignin = (error.config?.url || '').includes('/auth/signin')
  if (error.response?.status === 401 && !isSignin) {
    appConsoleService.error('AxiosService', 'the error response status is:' + error.response.status);

    // Signalée comme gérée : pas de message d'erreur brut, l'utilisateur est simplement renvoyé à la connexion
    error.sessionExpired = true
    error.message = SESSION_EXPIRED_MESSAGE;

    (async () => {
      appConsoleService.info('AxiosService', "Disconnect");
      await AuthService.logout()
      await NavigationService.goToLogin()
    })();
  }

  throw error
});

class AxiosService {

  constructor() {
    this.cacheStore = null
    this.ENDPOINT = null
    this.useCacheFirstStrategy = false

  }
  setEndPoint() {
    // Centralize endpoint construction
    const APP_PROTOCOL = AppEnvService.getEnv("APP_PROTOCOL");
    const APP_DOMAIN = AppEnvService.getEnv("APP_DOMAIN");
    this.ENDPOINT = `${APP_PROTOCOL}://${APP_DOMAIN}/api`;
  }

  /**
   * Initialise le store
   */
  initStore() {
    if (!this.cacheStore) {
      this.cacheStore = useCacheStore()
      this.cacheStore.init()
    }
  }

  /**
   * Vérifie si c'est une erreur réseau
   */
  isNetworkError(error) {
    return !error.response && (
        error.code === 'NETWORK_ERROR' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ERR_INTERNET_DISCONNECTED' ||
        error.code === 'ERR_NETWORK_IO_SUSPENDED' ||
        error.message.includes('Network Error') ||
        error.message.includes('fetch') ||
        error.message.includes('timeout')
    )
  }

  /**
   * Normalise les paramètres
   */
  normalizeParams(params) {
    if (!params || Object.keys(params).length === 0) {
      return {}
    }

    const normalized = {}
    Object.keys(params).sort().forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        normalized[key] = params[key]
      }
    })

    return normalized
  }

  /**
   * GET: Cache-first strategy
   */
  async get(target, config = {}) {
    this.initStore()
    const params = this.normalizeParams(config.params)

    const cacheFirstStrategy = AppEnvService.getEnv("CACHE_FIRST_STRATEGY")
    this.useCacheFirstStrategy = cacheFirstStrategy === '1'

    try {
      // --- NEW: ONLINE CACHE-FIRST LOGIC ---
      // Before anything else, check for a fresh, valid cache entry.
      if (networkService.isOnline) {
        const validCache = await this.cacheStore.getValidTTLEntries('GET', target, params);
        if (validCache && this.useCacheFirstStrategy) {
          appConsoleService.info('AxiosService', `VALID CACHE HIT: GET ${target} key=${validCache.key}`);
          return { ...validCache, config }; // Return the fresh data from cache
        }
      }

      // --- OFFLINE LOGIC (No change) ---
      if (!networkService.isOnline) {
        console.log(`OFFLINE MODE: Using stale cache for GET ${target}`)
        const cached = await this.cacheStore.getStale('GET', target, params)
        if (cached) {
          appConsoleService.info('AxiosService', `OFFLINE CACHE: GET ${target} key=${cached.key}`)
          return { ...cached, offline: true, config }
        } else {
          throw new Error(`Aucune donnée en cache pour ${target}`)
        }
      }

      // --- ONLINE NETWORK REQUEST (Fallback if no valid cache) ---
      Object.assign(config, { cancelToken: source.token })
      appConsoleService.info('AxiosService', 'GET (Network):' + target)

      this.setEndPoint()
      const response = await axios.get(this.ENDPOINT + target, config);

      // Set a TTL for GET requests so they can be reused while fresh
      // Let's use a 5-minute TTL as an example (300,000 ms)
      const fiveMinutesTTL = 5 * 60 * 1000;
      this.cacheStore.set('GET', target, params, response, fiveMinutesTTL)
      appConsoleService.info('AxiosService', `GET ${target} cached with TTL`)

      return response;

    } catch (error) {
      // --- NETWORK ERROR FALLBACK (No change) ---
      if (this.isNetworkError(error)) {
        console.log(`Network error for GET ${target}, falling back to stale cache`)
        const stale = await this.cacheStore.getStale('GET', target, params)
        if (stale) {
          appConsoleService.info('AxiosService', `NETWORK ERROR FALLBACK: GET ${target}`)
          return { ...stale, networkError: true, config }
        }
      }

      throw error;
    }
  }

  /**
   * POST: Cache-first strategy
   */
  async post(target, payload, config = {}) {
    this.initStore()
    const params = this.normalizeParams(config.params)

    // Écriture (create, update, delete, signin, updateGeo…) : jamais de cache, et impossible hors ligne
    if (!isCacheableRequest('POST', target)) {
      if (!networkService.isOnline) throw new Error('Connexion requise : action impossible hors ligne')
      Object.assign(config, { cancelToken: source.token })
      appConsoleService.info('AxiosService', 'POST (Network, sans cache):' + target)
      this.setEndPoint()
      return axios.post(this.ENDPOINT + target, payload, config)
    }

    const cacheFirstStrategy = AppEnvService.getEnv("CACHE_FIRST_STRATEGY")
    this.useCacheFirstStrategy = cacheFirstStrategy === '1'

    try {
      // --- NEW: ONLINE CACHE-FIRST LOGIC ---
      if (networkService.isOnline) {
        const validCache = await this.cacheStore.getValidTTLEntries('POST', target, params, payload);
        if (validCache && this.useCacheFirstStrategy) {
          appConsoleService.info('AxiosService', `VALID CACHE HIT: POST ${target}`);
          return { ...validCache, config };
        }
      }

      // --- OFFLINE LOGIC (No change) ---
      if (!networkService.isOnline) {
        console.log(`OFFLINE MODE: Using stale cache for POST ${target}`)
        const cached = await this.cacheStore.getStale('POST', target, params, payload)
        if (cached) {
          appConsoleService.info('AxiosService', `OFFLINE CACHE: POST ${target} key=${cached.key}`)
          return { ...cached, offline: true, config }
        } else {
          throw new Error(`Aucune donnée en cache pour POST ${target}`)
        }
      }

      // --- ONLINE NETWORK REQUEST (Fallback if no valid cache) ---
      Object.assign(config, { cancelToken: source.token })
      appConsoleService.info('AxiosService', 'POST (Network):' + target)

      this.setEndPoint()
      const response = await axios.post(this.ENDPOINT + target, payload, config);

      // POST requests often modify data, so they usually have a shorter TTL or none at all.
      // Here we set a short 1-minute TTL as an example.
      const fiveMinuteTTL = 5 * 60 * 1000;
      this.cacheStore.set('POST', target, params, response, fiveMinuteTTL, payload)
      appConsoleService.info('AxiosService', `POST ${target} cached with TTL`)

      return response;

    } catch (error) {
      // --- NETWORK ERROR FALLBACK (No change) ---
      if (this.isNetworkError(error)) {
        console.log(`Network error for POST ${target}, falling back to stale cache`)
        const stale = await this.cacheStore.getStale('POST', target, params, payload)
        if (stale) {
          appConsoleService.info('AxiosService', `NETWORK ERROR FALLBACK: POST ${target}`)
          return { ...stale, networkError: true, config }
        }
      }

      throw error;
    }
  }

  /**
   * Cancel all pending requests
   */
  cancel() {
    source.cancel('Operation canceled by the user.');
  }
}

export default new AxiosService();